import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

class GoogleTranslateErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Check if the error is related to Google Translate DOM manipulation
    const isGoogleTranslateError = 
      error.name === 'NotFoundError' ||
      error.message.includes('removeChild') ||
      error.message.includes('The node to be removed is not a child') ||
      error.stack?.includes('react-dom.production.min.js');

    if (isGoogleTranslateError) {
      console.warn('Google Translate DOM manipulation error caught:', error);
      return { hasError: true, error };
    }

    // Re-throw other errors to let them bubble up
    throw error;
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('GoogleTranslateErrorBoundary caught an error:', error, errorInfo);
  }

  componentDidMount() {
    // Add a global error handler for unhandled DOM errors
    window.addEventListener('error', this.handleGlobalError);
  }

  componentWillUnmount() {
    window.removeEventListener('error', this.handleGlobalError);
  }

  handleGlobalError = (event: ErrorEvent) => {
    const error = event.error;
    if (error && (
      error.name === 'NotFoundError' ||
      error.message?.includes('removeChild') ||
      error.message?.includes('The node to be removed is not a child')
    )) {
      event.preventDefault();
      console.warn('Global Google Translate error prevented:', error);
    }
  };

  render() {
    if (this.state.hasError) {
      // Force a re-render by resetting the error state after a short delay
      setTimeout(() => {
        this.setState({ hasError: false, error: undefined });
      }, 100);

      return this.props.fallback || null;
    }

    return this.props.children;
  }
}

export default GoogleTranslateErrorBoundary;
